"use client";

import { useRef, useState, type ReactNode } from "react";
import { Info, Lock, Save, ZoomIn } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export interface SectionModule {
  key: string;
  icon: ReactNode;
  title: string;
  /** Kurze, faktische Erklaerung fuer das Info-Popover (Glossar-Layer, siehe
   * backlog.md -> C14). */
  info: string;
  legend?: ReactNode;
  chart: ReactNode;
  /** Dateiname-Suffix fuer den PNG-Export (i.d.R. Aktien-Symbol + Modul-Key). */
  exportFileName: string;
  /** Premium-Gating (backlog.md -> "Monetarisierung"), fuer den Qualitätscheck-Tab:
   * "einzelne Charts verschwommen, nicht alle" statt einer Tab-weiten Sperre. Titel/Icon
   * bleiben sichtbar (zeigt, was fehlt), nur der Chart selbst wird geblurrt, Speichern/
   * Vergrößern sind dann sinnlos und werden deaktiviert. */
  locked?: boolean;
}

/**
 * Kachel-Chrome fuer das Kachel-Layout (backlog.md -> §A "Kachel-Layout"): Icon links,
 * Titel+Info-Popover mittig, Speichern+Lupe rechts, darunter optionale Legende, dann der
 * eigentliche Chart. Wickelt eine BESTEHENDE Chart-Komponente nur ein, veraendert sie
 * nicht inhaltlich. "Speichern" nutzt denselben html-to-image-Mechanismus wie
 * ScoreCardExport, "Lupe" oeffnet dieses eine Modul gross in einem Dialog (Radix) - die
 * anderen Kacheln bleiben unveraendert.
 */
export function ChartCard({ module }: { module: SectionModule }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);

  async function handleSave() {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // Dynamischer Import statt Top-Level-Import: html-to-image soll nicht das
      // initiale Bundle jeder Chart-Kachel vergroessern, wenn "Als Bild speichern"
      // ohnehin nur bei einem Bruchteil der Seitenaufrufe geklickt wird.
      const { toPng } = await import("html-to-image");
      if (document.fonts?.ready) await document.fonts.ready;
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      link.download = `erikonomie-${module.exportFileName}.png`;
      link.href = dataUrl;
      link.click();
    } catch {
      // Reine Komfortfunktion - bei Fehlschlag bleibt die Kachel unveraendert.
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <div ref={cardRef} className="rounded-lg border border-hairline bg-card p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className="text-ink-secondary" aria-hidden="true">
            {module.icon}
          </span>
          <div className="flex min-w-0 items-center gap-1.5">
            <h3 className="truncate text-sm font-semibold text-ink">{module.title}</h3>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* [Punkt 98] Touch-Zielflaeche vergroessert (14px-Icon ohne Padding war
                    zuvor die komplette Tap-Flaeche) - Padding erweitert den tatsaechlich
                    tappbaren Bereich, das kompensierende negative Margin haelt den
                    sichtbaren Abstand zu Titel/Save/Zoom in der Reihe unveraendert. */}
                <button
                  type="button"
                  aria-label={`Erklärung: ${module.title}`}
                  className="shrink-0 -m-1.5 p-1.5 text-primary hover:text-primary/80"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-64">{module.info}</TooltipContent>
            </Tooltip>
          </div>
          {/* p-1/-m-1 statt p-1.5 wie beim Info-Icon: die beiden Buttons sitzen hier direkt
              nebeneinander (gap-2 = 8px) - mit je 6px Padding pro Seite waeren die
              erweiterten Tap-Flaechen um 4px ueberlappend gewesen, 4px pro Seite (2×4=8px)
              stoesst dagegen exakt an der Gap-Grenze aneinander, ohne zu ueberlappen. */}
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={downloading || module.locked}
              aria-label="Als Bild speichern"
              className="-m-1 p-1 text-muted-foreground hover:text-ink disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setZoomOpen(true)}
              disabled={module.locked}
              aria-label="Vergrößern"
              className="-m-1 p-1 text-muted-foreground hover:text-ink disabled:opacity-50"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
          </div>
        </div>
        {module.legend}
        {module.locked ? (
          <div className="relative">
            <div aria-hidden="true" className="pointer-events-none select-none blur-sm">
              {module.chart}
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="flex size-8 items-center justify-center rounded-full bg-background/90 text-gold-text shadow-sm">
                <Lock className="size-3.5" aria-hidden="true" />
              </span>
            </div>
          </div>
        ) : (
          module.chart
        )}
      </div>

      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{module.title}</DialogTitle>
          </DialogHeader>
          {module.legend}
          {module.chart}
        </DialogContent>
      </Dialog>
    </>
  );
}
